type Step = {
	/**
	 * Whether the step can be considered
	 * a legal first step for the flow
	 */
	isRoot?: boolean;
	next?: readonly string[] | string;
};

export type FlowStep = Record<string, Step>;

export type FlatStep = [path: string, step: Step];

export type NextStepResult =
	| { reason: string; type: "error" }
	| { step: string; type: "step" }
	| { type: "complete" }
	| { type: "noop" };

export function flattenSteps(steps: readonly FlowStep[]): FlatStep[] {
	return steps.flatMap((step) => {
		return Object.entries(step);
	});
}

export function getRootSteps(flatSteps: readonly FlatStep[]): string[] {
	const roots = flatSteps
		.filter(([, step]) => {
			return step.isRoot;
		})
		.map(([path]) => {
			return path;
		});

	// The first step is always a valid entry point
	return [flatSteps[0]?.[0], ...roots].filter(Boolean);
}

export function findStepIndex(flatSteps: readonly FlatStep[], currentPath: string): number {
	return flatSteps.findIndex(([path]) => {
		return path === currentPath;
	});
}

export function resolveNextStep(
	flatSteps: readonly FlatStep[],
	currentIndex: number,
	requestedStep?: string,
): NextStepResult {
	if (currentIndex < 0 || currentIndex >= flatSteps.length) {
		return { type: "noop" };
	}

	const [currentPath, { next }] = flatSteps[currentIndex];

	if (!next) {
		return { type: "complete" };
	}

	if (typeof next === "string") {
		return { step: next, type: "step" };
	}

	if (!requestedStep) {
		return {
			reason: `flow.next() does not specify the step argument but the current step (${currentPath}) expects ${next.toString()}`,
			type: "error",
		};
	}

	const matchedStep = next.find((path) => {
		return path === requestedStep;
	});

	if (!matchedStep) {
		return {
			reason: `flow.next('${requestedStep}') does not exist in ${currentPath}. ${next.toString()} are the only available options`,
			type: "error",
		};
	}

	return { step: matchedStep, type: "step" };
}
